/**
 * Phone layout: a compact top bar, a horizontal tool strip, a status line and a bottom navigation
 * bar. The object list, properties and the menu open as bottom sheets over the canvas, so the
 * canvas stays visible for selecting while editing. Tablets and desktops never render this.
 */
import type { ReactNode } from 'react';
import { LANGS, fmtInt, t } from '../i18n/i18n';
import { type Tool, store, useApp } from '../state/store';
import { copySelected, cutSelected, deleteSelected, deleteSelectedNode, duplicateSelected, paste, saveCurrentProject, setLanguage, startNewProject } from './actions';
import { IconClose, IconCopy, IconCut, IconExport, IconFolder, IconImport, IconLayers, IconMore, IconNeedle, IconPaste, IconPatch, IconPlus, IconRedo, IconSave, IconSliders, IconTrash, IconUndo, IconUser } from './icons';

const saveWithToast = () => void saveCurrentProject().catch((e) => store.toast('error', String(e?.message ?? e)));

export function PhoneTopBar({ nameInput }: { nameInput: ReactNode }) {
  const dirty = useApp((s) => s.project.dirty);
  const canUndo = useApp((s) => s.history.past.length > 0);
  const canRedo = useApp((s) => s.history.future.length > 0);
  return (
    <header className="topbar">
      {nameInput}
      {dirty && <span className="dirty-dot" title={t('Unsaved changes')} aria-label={t('Unsaved changes')} />}
      <button className="btn ghost icon" aria-label={t('Undo')} disabled={!canUndo} onClick={() => store.undo()}>
        <IconUndo size={18} />
      </button>
      <button className="btn ghost icon" aria-label={t('Redo')} disabled={!canRedo} onClick={() => store.redo()}>
        <IconRedo size={18} />
      </button>
      <button className="btn primary icon" aria-label={t('Export')} onClick={() => store.set({ panel: 'export', drawer: null })}>
        <IconExport size={18} />
      </button>
    </header>
  );
}

export function PhoneTools({ tools }: { tools: { id: Tool; label: string; icon: ReactNode }[] }) {
  const tool = useApp((s) => s.tool);
  return (
    <div className="tools" role="toolbar" aria-label={t('Tools')} aria-orientation="horizontal">
      {tools.map((tl) => (
        <button key={tl.id} className="tool" aria-pressed={tool === tl.id} aria-label={t(tl.label)} onClick={() => store.set({ tool: tl.id, pick: null })}>
          {tl.icon}
        </button>
      ))}
    </div>
  );
}

/** One line of the most important numbers; errors open the export dialog, which lists them. */
export function PhoneStatus() {
  const result = useApp((s) => s.stitch.result);
  const st = result?.stats;
  const errors = result?.issues.filter((i) => i.severity === 'error').length ?? 0;
  const warnings = result?.issues.filter((i) => i.severity === 'warning').length ?? 0;
  return (
    <footer className="statusbar" aria-label={t('Design statistics')}>
      {st ? (
        <>
          <span className="stat">
            {t('Stitches')} <b>{fmtInt(st.stitches)}</b>
          </span>
          <span className="stat">
            <b>
              {st.widthMm.toFixed(1)} × {st.heightMm.toFixed(1)} mm
            </b>
          </span>
          <span className="spacer" />
          {errors > 0 && (
            <button className="badge err" onClick={() => store.set({ panel: 'export', drawer: null })}>
              {t('{n} error(s)', { n: errors })}
            </button>
          )}
          {!errors && warnings > 0 && <span className="badge warn">{t('{n} warning(s)', { n: warnings })}</span>}
          {!errors && !warnings && st.stitches > 0 && <span className="badge ok">{t('Ready for export')}</span>}
        </>
      ) : (
        <span>{t('No stitches yet')}</span>
      )}
    </footer>
  );
}

export function PhoneNav() {
  const drawer = useApp((s) => s.drawer);
  const mode = useApp((s) => s.mode);
  const toggle = (d: 'layers' | 'inspector' | 'menu') => store.set((s) => ({ drawer: s.drawer === d ? null : d }));
  return (
    <nav className="phone-nav" aria-label={t('Menu')}>
      <button aria-pressed={drawer === 'layers'} onClick={() => toggle('layers')}>
        <IconLayers size={20} />
        <span>{t('Objects')}</span>
      </button>
      <button aria-pressed={drawer === 'inspector'} onClick={() => toggle('inspector')}>
        <IconSliders size={20} />
        <span>{t('Properties')}</span>
      </button>
      <button aria-pressed={mode === 'simulate'} onClick={() => store.set((s) => ({ mode: s.mode === 'simulate' ? 'edit' : 'simulate', pick: null, drawer: null }))}>
        <IconNeedle size={20} />
        <span>{t('Simulate')}</span>
      </button>
      <button onClick={() => store.set({ panel: 'import', drawer: null })}>
        <IconImport size={20} />
        <span>{t('Import')}</span>
      </button>
      <button aria-pressed={drawer === 'menu'} onClick={() => toggle('menu')}>
        <IconMore size={20} />
        <span>{t('More')}</span>
      </button>
    </nav>
  );
}

/** Bottom sheet with what the desktop top bar and gear menu hold. */
export function PhoneMenu() {
  const mode = useApp((s) => s.mode);
  const layers = useApp((s) => s.layers);
  const hasImage = useApp((s) => !!s.image);
  const lang = useApp((s) => s.lang);
  const account = useApp((s) => s.account);
  const close = () => store.set({ drawer: null });
  const setLayer = (k: keyof typeof layers) => store.set((s) => ({ layers: { ...s.layers, [k]: !s.layers[k] } }));
  return (
    <section className="phone-sheet phone-menu" aria-label={t('More')}>
      <div className="sheet-head">
        <h2>{t('More')}</h2>
        <button className="btn icon ghost" aria-label={t('Close')} onClick={close}>
          <IconClose />
        </button>
      </div>
      <div className="sheet-body">
        <div className="menu-grid">
          <button
            className="btn"
            onClick={() => {
              close();
              startNewProject();
            }}
          >
            <IconPlus size={16} /> {t('New project')}
          </button>
          <button className="btn" onClick={() => store.set({ panel: 'projects', drawer: null })}>
            <IconFolder size={16} /> {t('Projects')}
          </button>
          <button
            className="btn"
            onClick={() => {
              close();
              saveWithToast();
            }}
          >
            <IconSave size={16} /> {t('Save')}
          </button>
          <button className="btn" onClick={() => store.set({ panel: 'patch', drawer: null })}>
            <IconPatch size={16} /> {t('Patch')}
          </button>
          {account.enabled && (
            <button className="btn" onClick={() => store.set({ panel: 'account', drawer: null })}>
              <IconUser size={16} /> {account.user ? t('Account') : t('Log in')}
            </button>
          )}
        </div>
        {mode === 'edit' && (
          <>
            <h3>{t('Canvas layers')}</h3>
            <div className="segmented" role="group" aria-label={t('Canvas layers')}>
              <button aria-pressed={layers.image} disabled={!hasImage} onClick={() => setLayer('image')}>
                {t('Image')}
              </button>
              <button aria-pressed={layers.shapes} onClick={() => setLayer('shapes')}>
                {t('Shapes')}
              </button>
              <button aria-pressed={layers.stitches} onClick={() => setLayer('stitches')}>
                {t('Stitches')}
              </button>
              <button aria-pressed={layers.jumps} onClick={() => setLayer('jumps')}>
                {t('Jumps')}
              </button>
            </div>
          </>
        )}
        <h3>{t('Language')}</h3>
        <div className="menu-grid" role="group" aria-label={t('Language')}>
          {LANGS.map((l) => (
            <button key={l.id} className="btn" aria-pressed={lang === l.id} onClick={() => setLanguage(l.id)}>
              {l.name}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Actions that are keyboard shortcuts on the desktop (copy, cut, paste, delete node) as buttons
 * over the canvas. Shown only when they apply to the current selection or tool.
 */
export function PhoneActionBar() {
  const tool = useApp((s) => s.tool);
  const hasSelection = useApp((s) => s.selection.length > 0);
  const hasNode = useApp((s) => s.tool === 'node' && !!s.selectedNode);
  const hasClipboard = useApp((s) => s.clipboard.length > 0);
  const actions: { label: string; icon: ReactNode; run: () => void; danger?: boolean }[] = [];
  if (hasNode) actions.push({ label: t('Delete node'), icon: <IconTrash size={18} />, run: () => deleteSelectedNode(), danger: true });
  else if (hasSelection && tool === 'select') {
    actions.push(
      { label: t('Copy'), icon: <IconCopy size={18} />, run: () => copySelected() },
      { label: t('Cut'), icon: <IconCut size={18} />, run: () => cutSelected() },
      { label: t('Duplicate'), icon: <IconPlus size={18} />, run: () => duplicateSelected() },
      { label: t('Delete'), icon: <IconTrash size={18} />, run: () => deleteSelected(), danger: true },
    );
  }
  if (hasClipboard && tool === 'select') actions.push({ label: t('Paste'), icon: <IconPaste size={18} />, run: () => paste() });
  if (!actions.length) return null;
  return (
    <div className="phone-actions" role="toolbar" aria-label={t('Edit')}>
      {actions.map((a) => (
        <button key={a.label} className={a.danger ? 'danger' : undefined} onClick={a.run}>
          {a.icon}
          <span>{a.label}</span>
        </button>
      ))}
    </div>
  );
}

/** Close button for the bottom sheets (object list, properties). */
export function SheetClose() {
  return (
    <button className="btn icon ghost sheet-close" aria-label={t('Close')} onClick={() => store.set({ drawer: null })}>
      <IconClose />
    </button>
  );
}

/** Dims the screen above an open sheet; a tap there closes the sheet. The navigation bar stays usable. */
export function SheetBackdrop() {
  const open = useApp((s) => s.drawer !== null);
  if (!open) return null;
  return <div className="sheet-backdrop" aria-hidden="true" onClick={() => store.set({ drawer: null })} />;
}
